import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { XCircleIcon, PhotoIcon } from '@heroicons/react/24/outline';
import { uploadFiles } from '../../../utils/uploadUtils';

const ReviewPhotoUploader = ({ onUploadPics, error, initialPhotos = [] }) => {
  const [photos, setPhotos] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [replacingIndex, setReplacingIndex] = useState(null);
  
  // Initialize with existing photos in edit mode
  useEffect(() => {
    if (initialPhotos?.length > 0) {
      setPhotos(initialPhotos);
    }
  }, [initialPhotos]);

  const handlePhotoChange = async (files) => {
    if (!files || files.length === 0) {
      toast.error('Please select at least one image file');
      return;
    }

    const validFiles = Array.from(files).filter(file => file.type.startsWith('image/'));
    console.log('validFiles', validFiles);

    if (validFiles.length !== files.length) {
      toast.error('Please upload only image files');
      return;
    }

    // Set loading state
    setIsUploading(true);

    try {
      // Use the direct upload utility instead of Redux
      const uploadedPhotos = await uploadFiles(validFiles);
      
      console.log('uploadedPhotos response:', uploadedPhotos);
      const newPhotos = [...photos, ...uploadedPhotos];
      setPhotos(newPhotos);
      onUploadPics(newPhotos);
    } catch (error) {
      console.error('Failed to upload photos:', error);
      const errorMessage = error.message || 'Failed to upload photos';
      toast.error(errorMessage);
    } finally {
      // Always set loading state back to false
      setIsUploading(false);
    }
  };

  // Function to replace a specific photo
  const handleReplacePhoto = async (file, index) => {
    if (!file || !file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    setReplacingIndex(index);
    setIsUploading(true);

    try {
      const uploadedPhoto = await uploadFiles([file]);
      if (uploadedPhoto && uploadedPhoto.length > 0) {
        const newPhotos = [...photos];
        newPhotos[index] = uploadedPhoto[0];
        setPhotos(newPhotos);
        onUploadPics(newPhotos);
      }
    } catch (error) {
      console.error('Failed to replace photo:', error);
      const errorMessage = error.message || 'Failed to replace photo';
      toast.error(errorMessage);
    } finally {
      setIsUploading(false);
      setReplacingIndex(null);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    handlePhotoChange(files);
  };

  const handleDelete = (index) => {
    const newPhotos = [...photos];
    newPhotos.splice(index, 1);
    setPhotos(newPhotos);
    onUploadPics(newPhotos);
  };

  return (
    <div className="space-y-4">
      <div 
        className={`border-2 border-dashed p-8 text-center ${isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300'} ${error ? 'border-red-500' : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <input
          type="file"
          id="review-photo-upload"
          multiple
          className="hidden"
          accept="image/*"
          onChange={(e) => handlePhotoChange(e.target.files)}
          disabled={isUploading}
        />
        <label
          htmlFor="review-photo-upload"
          className={`cursor-pointer block ${isUploading ? 'opacity-50' : ''}`}
        >
          {isUploading ? (
            <div className="text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
              <p className="mt-2 text-sm text-gray-600">Uploading...</p>
            </div>
          ) : (
            <>
              <div className="text-gray-500 mb-2">
                <PhotoIcon className="mx-auto h-12 w-12" />
              </div>
              <p className="text-sm text-gray-600">Drag photos here or click to upload</p>
              <p className="text-xs text-gray-500 mt-1">PNG, JPG up to 5MB</p>
            </>
          )}
        </label>
      </div>
      
      {error && <p className="text-red-500 text-sm">{error}</p>}

      {photos.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
          {photos.map((photo, index) => (
            <div 
              key={photo.id || index} 
              className="relative group rounded-lg overflow-hidden border"
            >
              <input
                type="file"
                id={`replace-review-photo-${index}`}
                className="hidden"
                accept="image/*"
                onChange={(e) => handleReplacePhoto(e.target.files[0], index)}
              />
              <img 
                src={photo.url || photo.formats?.thumbnail?.url || photo.formats?.small?.url || photo.formats?.medium?.url}
                alt={`Uploaded ${index + 1}`}
                className="w-full h-32 object-contain bg-gray-100"
              />
              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-60 transition-all flex flex-col items-center justify-center opacity-0 group-hover:opacity-100">
                <div className="w-full px-2 flex flex-col space-y-2">
                  <button
                    type="button"
                    onClick={() => handleDelete(index)}
                    className="w-full bg-red-600 text-white py-1 px-2 rounded text-xs flex items-center justify-center"
                    title="Delete photo"
                  >
                    <XCircleIcon className="h-4 w-4 mr-1" />
                    Delete
                  </button>

                  <label
                    htmlFor={`replace-review-photo-${index}`}
                    className="w-full bg-yellow-500 text-white py-1 px-2 rounded text-xs flex items-center justify-center cursor-pointer"
                    title="Replace photo"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                    </svg>
                    Replace
                  </label>
                </div>
              </div>
              {replacingIndex === index && (
                <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-white"></div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ReviewPhotoUploader; 